import { cn, tv, VariantProps } from "@utils/tw"
import { ComponentProps, forwardRef } from "react"
import { Icon, IconRef } from "./Icon"
import { Spinner } from "./Spinner"

type InputProps = Omit<ComponentProps<"input">, "size"> &
  VariantProps<typeof inputStyles> & {
    leftIcon?: IconRef
    rightIcon?: IconRef
    onValueChange?: (value: string) => void
    iosFocusFix?: boolean
  }

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    { className, type, leftIcon, rightIcon, loading, stretch, onValueChange, variant, shape, iosFocusFix, ...props },
    ref,
  ) => {
    const styles = inputStyles({
      variant,
      loading,
      stretch,
      shape,
      iosFocusFix,
    })
    return (
      <div className={cn(styles.base(), className)}>
        {leftIcon ? <Icon data-part="icon" name={leftIcon} /> : null}
        <input
          type={type}
          className={cn(styles.input())}
          ref={ref}
          {...props}
          onChange={
            onValueChange
              ? (e) => {
                  onValueChange!(e.target.value)
                  props.onChange?.(e)
                }
              : props.onChange
          }
        />
        {loading ? <Spinner data-part="spinner" /> : null}
        {rightIcon ? <Icon data-part="icon" name={rightIcon} /> : null}
      </div>
    )
  },
)
Input.displayName = "Input"

export const inputStyles = tv({
  base: [
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[12px] md:rounded-md relative",
    "type-ui-l transition-colors",
    "focus-within:ring-input disabled:pointer-events-none disabled:opacity-50",
    "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
    "first:part-[icon]:-ml-1",
    "last:part-[icon]:-mr-1",
    "[&_.icon]:size-[1.4em] part-[spinner]:size-[1.3em]",
    "part-[icon]:flex-none",
    "part-[spinner]:absolute part-[spinner]:top-1/2 part-[spinner]:left-1/2 part-[spinner]:transform part-[spinner]:-translate-x-1/2 part-[spinner]:-translate-y-1/2",
  ],
  slots: {
    input:
      "max-sm:[font-size:16px!important] h-full flex-1 outline-none border-0 !bg-transparent [&:is(:-webkit-autofill,_:autofill)]:[-webkit-box-shadow:_0_0_0_50px_rgb(var(--color-bg-low)/1)_inset] autofill-fix placeholder:text-fg-secondary",
  },
  variants: {
    variant: {
      default: "bg-bg-low text-fg",
    },
    shape: {
      default: "h-control px-4 type-ui-m",
      sm: "h-control px-[0.8em]",
      icon: "h-control w-control",
    },
    loading: {
      true: ["pointer-events-none opacity-90", "[&_>_*]:opacity-0 part-[spinner]:opacity-100"],
    },
    stretch: {
      true: "flex-1",
    },
    iosFocusFix: {
      true: {
        input: "no-focus-scroll-ios",
      },
    },
  },
  defaultVariants: {
    shape: "default",
    variant: "default",
  },
})
